import React, { useEffect, useState } from "react";
import { api, formatApiError, formatDateFR } from "../../../lib/api";
import { toast } from "sonner";
import { Loader2, KeyRound } from "lucide-react";
import { Forbidden } from "./TeamSection";

const inputCls = "w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-violet-600 outline-none";
const labelCls = "text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500";

// ─── Sécurité : changement de mot de passe ────────────────────────────────────
export function SecuritySection() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) { toast.error("Les deux mots de passe ne correspondent pas"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Mot de passe mis à jour");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="security-section">
      <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6 max-w-md space-y-3">
        <div className="overline">Changer mon mot de passe</div>
        <div>
          <label className={labelCls}>Mot de passe actuel *</label>
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} className={inputCls + " mt-1"} data-testid="security-current-password" />
        </div>
        <div>
          <label className={labelCls}>Nouveau mot de passe (8 caractères min.) *</label>
          <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className={inputCls + " mt-1"} data-testid="security-new-password" />
        </div>
        <div>
          <label className={labelCls}>Confirmer le nouveau mot de passe *</label>
          <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className={inputCls + " mt-1"} data-testid="security-confirm-password" />
        </div>
        <button type="submit" disabled={saving} className="inline-flex items-center gap-2 h-10 px-5 rounded-xl bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 disabled:opacity-50" data-testid="security-submit">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" />} Mettre à jour
        </button>
      </form>
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 max-w-md text-[11px] text-slate-500">
        La double authentification (2FA), la liste des sessions ouvertes et la déconnexion des autres appareils arriveront dans une prochaine phase.
      </div>
    </div>
  );
}

// ─── Journal d'activité ───────────────────────────────────────────────────────
export function ActivitySection() {
  const [entries, setEntries] = useState(null);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    api.get("/vendor/team/activity?limit=200")
      .then((r) => setEntries(r.data.entries || []))
      .catch((e) => { if (e.response?.status === 403) setForbidden(true); setEntries([]); });
  }, []);

  if (forbidden) return <Forbidden />;
  if (entries === null) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>;

  return (
    <div className="space-y-3" data-testid="activity-section">
      <div className="text-sm text-slate-500">Journal transversal — connexions, gestion de l'équipe, rôles et mots de passe. (Sera étendu aux actions métier en Phase 2.)</div>
      {entries.length === 0 ? (
        <div className="py-16 text-center text-sm text-slate-400 rounded-2xl border border-dashed border-slate-200" data-testid="activity-empty">Aucune activité enregistrée pour le moment.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Quand</th>
                  <th className="text-left px-5 py-3 font-semibold">Qui</th>
                  <th className="text-left px-5 py-3 font-semibold">Action</th>
                  <th className="text-left px-5 py-3 font-semibold">Cible</th>
                  <th className="text-left px-5 py-3 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {entries.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50/60" data-testid={`activity-row-${e.id}`}>
                    <td className="px-5 py-3 whitespace-nowrap text-xs text-slate-500">{formatDateFR((e.at || "").substring(0, 10))} {(e.at || "").substring(11, 16)}</td>
                    <td className="px-5 py-3 whitespace-nowrap font-medium text-slate-800 text-xs">{e.user_name}</td>
                    <td className="px-5 py-3 text-xs text-slate-700">{e.action}{e.detail ? <span className="text-slate-400"> — {e.detail}</span> : null}</td>
                    <td className="px-5 py-3 text-xs text-slate-500 max-w-[180px] truncate">{e.entity_ref || "—"}</td>
                    <td className="px-5 py-3 text-[11px] text-slate-400 font-mono">{e.ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
